import { safeDisplayText } from '@/lib/utils';

interface SeoChecklistContentProps {
  templates: Array<{
    id: string;
    template_name: string;
    phases: Array<{
      id: string;
      phase_title: string;
      items: Array<{
        id: string;
        item_text: string;
        notes?: string | null;
      }>;
    }>;
  }>;
}

/**
 * SEO-optimized hidden content component that renders template checklist data
 * in semantic HTML for search engine crawlers and screen readers.
 * 
 * This component is visually hidden but accessible to:
 * - Search engines (Google, Bing, etc.) for indexing
 * - Screen readers for improved accessibility
 * 
 * It ensures that checklist items appear in search results while maintaining
 * the interactive carousel UI for visual users.
 */
export const SeoChecklistContent = ({ templates }: SeoChecklistContentProps) => {
  if (!templates || templates.length === 0) {
    return null;
  }

  return (
    <section 
      className="sr-only" 
      aria-label="Complete checklist contents for search engines and screen readers"
    >
      <h2>Triathlon Race Preparation Checklists</h2>
      <p>
        Comprehensive triathlon checklists covering everything you need for race day preparation, 
        training, transition setup, nutrition, and post-race recovery.
      </p>
      
      {templates.map(template => (
        <article key={template.id}>
          <h3>{safeDisplayText(template.template_name)}</h3>
          
          {template.phases.map(phase => (
            <section key={phase.id}>
              <h4>{safeDisplayText(phase.phase_title)}</h4>
              
              {phase.items.length > 0 ? (
                <ul>
                  {phase.items.map(item => (
                    <li key={item.id}>
                      {safeDisplayText(item.item_text)}
                      {item.notes && ` - ${safeDisplayText(item.notes)}`}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No items in this phase.</p>
              )}
            </section>
          ))}
        </article>
      ))}
    </section>
  );
};
